import { useRef } from 'react'

/**
 * A reusable hook for infinite scroll using IntersectionObserver.
 *
 * @example
 * const { loadMoreRef } = useInfiniteScroll({
 *   hasNextPage,
 *   isFetchingNextPage,
 *   fetchNextPage,
 * })
 *
 * // In your render:
 * <div ref={loadMoreRef} className="py-2">
 *   {isFetchingNextPage && <Loader />}
 * </div>
 */
export function useInfiniteScroll(props: {
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => void
  rootMargin?: string
  threshold?: number
}) {
  const observerRef = useRef<IntersectionObserver | null>(null)

  const loadMoreRef = (node: HTMLDivElement | null) => {
    if (props.isFetchingNextPage) return

    if (observerRef.current) {
      observerRef.current.disconnect()
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && props.hasNextPage) {
          props.fetchNextPage()
        }
      },
      { rootMargin: props.rootMargin ?? '50px', threshold: props.threshold ?? 0 },
    )

    if (node) {
      observerRef.current.observe(node)
    }
  }

  return { loadMoreRef }
}
